/**
 * @quotech/ui — Button
 *
 * Small, reusable Tailwind button primitive shared across all hubs.
 * Uses the design tokens the rest of the app relies on (bg-primary /
 * text-primary-foreground / hover:bg-accent / focus:ring-ring), so it
 * looks native anywhere it's dropped in.
 *
 * FLOW: props (variant/size + native button attrs) → cn() class merge → <button>
 *
 * USAGE:
 *   import { Button } from '@quotech/ui'
 *   <Button variant="outline" size="sm" onClick={…}>Save</Button>
 *
 * NOTE: a richer shadcn/cva Button lives at ./ui/button (internal, not
 * exported from the package barrel). This is the lightweight public
 * primitive for the P2 migration.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/utils'

export type ButtonVariant = 'default' | 'outline' | 'ghost'
export type ButtonSize = 'sm' | 'md'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  outline: 'border border-border bg-background hover:bg-accent hover:text-accent-foreground',
  ghost: 'bg-transparent hover:bg-accent hover:text-accent-foreground',
}

const SIZE_STYLES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
}

/**
 * Default `type="button"` so a Button inside a <form> never triggers an
 * accidental submit; callers opt into submit explicitly via `type="submit"`.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'md', type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none disabled:opacity-50',
        VARIANT_STYLES[variant],
        SIZE_STYLES[size],
        className,
      )}
      {...props}
    />
  ),
)
Button.displayName = 'Button'
